import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Badge, Alert, Spinner, Form, Button } from 'react-bootstrap';
import {
    FaCalendarAlt, FaTshirt, FaTools, FaChartBar, FaClipboardList,
    FaCheck, FaBan, FaInfoCircle, FaLock, FaUnlock, FaShieldAlt,
    FaExclamationTriangle, FaLink,
} from 'react-icons/fa';
import api from '../axiosSetup';
import Swal from 'sweetalert2';
import CustomNavbar from '../CustomNavbar';
import cfg from '../../config';

// ── Modul-Metadaten ───────────────────────────────────────────────────────────
// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const MODULES = () => [
    {
        key: 'Modul_Veranstaltungen',
        licenseKey: 'modul_veranstaltungen',
        label: `Termine, ${cfg.eventTermPlural} & QR-Codes`,
        icon: FaCalendarAlt,
        color: '#4e9af1',
        gradient: 'linear-gradient(135deg, #1a2a4a 0%, #0d1b2e 100%)',
        features: [
            { label: `Liste der ${cfg.eventTermPlural}`, desc: `${cfg.memberTermPlural} können ${cfg.eventTermPlural} sehen und buchen` },
            { label: 'Kalender', desc: 'Jahreskalender mit allen Terminen' },
            { label: 'iCal-Export', desc: 'Kalender als .ics-Datei herunterladen' },
            { label: 'Buchungsübersicht (ZR)', desc: `${cfg.adminRoleName} sieht alle Buchungen` },
            { label: `Verwaltung der ${cfg.eventTermPlural}`, desc: `${cfg.adminRoleName} kann ${cfg.eventTermPlural} anlegen und bearbeiten` },
            { label: 'Mein QR-Code', desc: `${cfg.memberTermPlural} sehen ihren persönlichen QR-Code` },
            { label: 'QR-Code Scanner (ZR)', desc: `${cfg.adminRoleName} kann QR-Codes einscannen und bestätigen` },
            { label: 'QR-Code Audit-Log (Admin)', desc: 'Admin sieht alle QR-Code-Scans' },
        ],
    },
    {
        key: 'Modul_Haes',
        licenseKey: 'modul_haes',
        label: cfg.costumeTermPlural,
        icon: FaTshirt,
        color: '#f1a24e',
        gradient: 'linear-gradient(135deg, #2e1a0a 0%, #1e1008 100%)',
        features: [
            { label: `Bewerbung (${cfg.costumeTerm})`, desc: `${cfg.memberTermPlural} können sich bewerben` },
            { label: `Verwaltung der ${cfg.costumeTermPlural}`, desc: `${cfg.adminRoleName} verwaltet alle ${cfg.costumeTermPlural}` },
            { label: 'Bewerbungsbearbeitung (ZR)', desc: `${cfg.adminRoleName} bearbeitet eingehende Bewerbungen` },
            { label: `${cfg.costumeTerm}typen & Manager`, desc: `Verwaltung der ${cfg.costumeTerm}typen und ihrer Verantwortlichen` },
        ],
    },
    {
        key: 'Modul_Arbeitseinsatz',
        licenseKey: 'modul_arbeitseinsatz',
        label: 'Arbeitseinsatz & Tauschbörse',
        icon: FaTools,
        color: '#4ef16a',
        gradient: 'linear-gradient(135deg, #0a2e12 0%, #071a0c 100%)',
        requires: ['Modul_Haes'],
        features: [
            { label: 'Tauschbörse', desc: `${cfg.memberTermPlural} können Arbeitseinsätze tauschen` },
            { label: 'Arbeitseinsatz-Verwaltung (ZR)', desc: `${cfg.adminRoleName} erstellt und verwaltet Arbeitseinsätze` },
            { label: 'Tauschbörse-Übersicht (ZR)', desc: `${cfg.adminRoleName} sieht alle Tauschgesuche` },
        ],
    },
    {
        key: 'Modul_Berichte',
        licenseKey: 'modul_berichte',
        label: 'Berichte & Statistiken',
        icon: FaChartBar,
        color: '#c44ef1',
        gradient: 'linear-gradient(135deg, #1e0a2e 0%, #120618 100%)',
        requires: ['Modul_Veranstaltungen'],
        features: [
            { label: 'Statistiken', desc: 'Anwesenheitsquoten und Auswertungen' },
            { label: 'Jahresbericht CSV', desc: 'Export als CSV für Excel' },
            { label: 'Personalakten', desc: `${cfg.adminRoleName} führt Personalakten` },
        ],
    },
    {
        key: 'Modul_Tasks',
        licenseKey: 'modul_tasks',
        label: 'Aufgabenverwaltung',
        icon: FaClipboardList,
        color: '#38bdf8',
        gradient: 'linear-gradient(135deg, #0a1e2e 0%, #060d17 100%)',
        features: [
            { label: 'Aufgaben erstellen', desc: `${cfg.adminRoleName} erstellt und weist Aufgaben zu` },
            { label: 'Aufgaben verwalten', desc: 'Bearbeiten, Priorisieren und Löschen von Aufgaben' },
            { label: 'Meine Aufgaben', desc: `${cfg.memberTermPlural} sehen ihre zugewiesenen Aufgaben` },
            { label: 'Status-Tracking', desc: 'Aufgaben als Offen, In Bearbeitung oder Erledigt markieren' },
            { label: 'Push-Benachrichtigung', desc: 'Automatische Benachrichtigung bei Zuweisung (59€/Jahr)' },
        ],
    },
];

const MODULE_BY_KEY = () => Object.fromEntries(MODULES().map(m => [m.key, m]));
const MODULE_DEPENDENTS = {
    Modul_Haes:            ['Modul_Arbeitseinsatz'],
    Modul_Veranstaltungen: ['Modul_Berichte'],
};

// Lizenz-Status-Anzeigedaten
const LICENSE_STATUS_UI = {
    valid:    { color: '#4ef16a', icon: '✓',  label: 'Gültig',               bg: 'rgba(78,241,106,0.07)'  },
    expiring: { color: '#facc15', icon: '⏰', label: 'Läuft bald ab',        bg: 'rgba(250,204,21,0.07)'  },
    expired:  { color: '#f87171', icon: '✕',  label: 'Abgelaufen',           bg: 'rgba(248,113,113,0.10)' },
    invalid:  { color: '#f87171', icon: '⚠',  label: 'Ungültig',             bg: 'rgba(248,113,113,0.10)' },
    missing:  { color: '#f87171', icon: '⚠',  label: 'Nicht konfiguriert',   bg: 'rgba(248,113,113,0.10)' },
};

export default function AdminFeatures() {
    const [entries, setEntries]         = useState({});
    const [loading, setLoading]         = useState(true);
    const [toggling, setToggling]       = useState(null);
    const [error, setError]             = useState('');
    const [license, setLicense]         = useState(null);  // from /api/license/status
    const [licenseLoading, setLicenseLoading] = useState(true);
    // Notsperre — härter als der Wartungsmodus, betrifft auch den Login
    const [systemLocked, setSystemLocked] = useState(false);
    const [lockMessage, setLockMessage]   = useState('');
    const [lockBusy, setLockBusy]         = useState(false);

    const toggleSystemLock = async (locked) => {
        if (locked) {
            const { isConfirmed } = await Swal.fire({
                title: 'Zugang wirklich sperren?',
                html: 'Danach kann sich <b>niemand mehr anmelden</b> — auch du selbst nicht, solange du ausgeloggt bist.<br/><br/>'
                    + '<span style="opacity:.75;font-size:.9em">Zum Aufheben brauchst du eine bestehende Sitzung oder Zugriff auf die Datenbank '
                    + '(<code>tblconfig.System_Locked</code> auf <code>0</code> setzen).</span>',
                icon: 'warning', showCancelButton: true,
                confirmButtonText: 'Ja, sperren', cancelButtonText: 'Abbrechen',
                confirmButtonColor: '#dc3545', background: '#1e2130', color: '#fff',
            });
            if (!isConfirmed) return;
        }
        setLockBusy(true);
        try {
            await api.put('/api/admin/system-lock', { locked, message: lockMessage });
            setSystemLocked(locked);
            Swal.fire({
                icon: locked ? 'warning' : 'success',
                title: locked ? 'Zugang gesperrt' : 'Sperre aufgehoben',
                background: '#1e2130', color: '#fff', timer: 2000, showConfirmButton: false,
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Die Sperre konnte nicht geändert werden.');
        } finally {
            setLockBusy(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await api.get('/api/admin/config');
            const map = {};
            data.forEach(e => { map[e.name] = e; });
            setEntries(map);
            setSystemLocked(map['System_Locked']?.value === '1');
            setLockMessage(map['System_LockedMessage']?.value || '');
        } catch {
            setError('Konfiguration konnte nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadLicense = useCallback(async () => {
        setLicenseLoading(true);
        try {
            const { data } = await api.get('/api/license/status');
            setLicense(data);
        } catch {
            setLicense({ status: 'missing', message: '' });
        } finally {
            setLicenseLoading(false);
        }
    }, []);

    useEffect(() => { load(); loadLicense(); }, [load, loadLicense]);

    // Ist ein Modul in der aktuellen Lizenz enthalten?
    const isLicensed = (mod) => {
        if (!license) return true; // noch laden
        if (!['valid', 'expiring', 'expired'].includes(license.status)) return true; // Demo-Modus
        return (license.licensedModules || []).some(lm => lm.featureKey === mod.licenseKey);
    };

    const toggle = async (moduleKey) => {
        const entry = entries[moduleKey];
        if (!entry) return;
        const mod     = MODULE_BY_KEY()[moduleKey];
        const newVal  = entry.value === '1' ? '0' : '1';

        // Lizenzprüfung
        if (newVal === '1' && !isLicensed(mod)) {
            await Swal.fire({
                title: 'Modul nicht lizenziert',
                html: `Das Modul <strong>${mod.label}</strong> ist in der aktuellen Lizenz nicht enthalten.<br><br><small class="text-muted">Bitte wende dich an deinen Anbieter für eine Lizenz-Erweiterung.</small>`,
                icon: 'error',
                confirmButtonText: 'OK',
                background: '#1a1a1a', color: '#fff',
            });
            return;
        }

        // Abhängigkeits-Hinweis beim Aktivieren
        if (newVal === '1' && mod.requires?.length) {
            const missing = mod.requires.filter(dep => entries[dep]?.value !== '1');
            if (missing.length) {
                const depLabels = missing.map(k => MODULE_BY_KEY()[k]?.label || k).join(', ');
                const result = await Swal.fire({
                    title: 'Abhängigkeit wird aktiviert',
                    html: `<strong>${mod.label}</strong> benötigt:<br><ul style="text-align:left;margin-top:8px">${missing.map(k => `<li>${MODULE_BY_KEY()[k]?.label || k}</li>`).join('')}</ul>Diese Module werden automatisch mit aktiviert.`,
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: 'Aktivieren',
                    cancelButtonText: 'Abbrechen',
                    background: '#1a1a1a', color: '#fff',
                });
                if (!result.isConfirmed) return;
            }
        }

        // Abhängigkeits-Hinweis beim Deaktivieren
        if (newVal === '0') {
            const dependents = (MODULE_DEPENDENTS[moduleKey] || []).filter(dep => entries[dep]?.value === '1');
            const label = mod.label;
            const html = dependents.length
                ? `<ul style="text-align:left;margin-top:8px">${dependents.map(k => `<li>${MODULE_BY_KEY()[k]?.label || k}</li>`).join('')}</ul>Diese Module werden ebenfalls deaktiviert.<br><br><small class="text-muted">Bestehende Daten bleiben erhalten.</small>`
                : `<small class="text-muted">Bestehende Daten bleiben erhalten.</small>`;

            const result = await Swal.fire({
                title: `"${label}" deaktivieren?`,
                html,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: 'Deaktivieren',
                cancelButtonText: 'Abbrechen',
                background: '#1a1a1a', color: '#fff',
            });
            if (!result.isConfirmed) return;
        }

        setToggling(moduleKey);
        try {
            const { data } = await api.put(`/api/admin/config/${entry.id}`, { value: newVal });

            // Eigenen Eintrag aktualisieren
            setEntries(prev => ({
                ...prev,
                [moduleKey]: { ...prev[moduleKey], value: newVal },
            }));

            // Kaskadierte Module aktualisieren
            if (data.cascaded?.length) {
                setEntries(prev => {
                    const next = { ...prev };
                    data.cascaded.forEach(({ name, action }) => {
                        if (next[name]) next[name] = { ...next[name], value: action === 'enabled' ? '1' : '0' };
                    });
                    return next;
                });

                const cascadeLabels = data.cascaded.map(c => MODULE_BY_KEY()[c.name]?.label || c.name);
                const action = newVal === '1' ? 'mit aktiviert' : 'mit deaktiviert';
                Swal.fire({
                    toast: true, position: 'top-end', showConfirmButton: false, timer: 3500,
                    icon: 'info',
                    title: `Auch ${action}: ${cascadeLabels.join(', ')}`,
                    background: '#1a2040', color: '#fff',
                });
            }
        } catch (err) {
            Swal.fire('Fehler', err.response?.data?.message || 'Speichern fehlgeschlagen', 'error');
        } finally {
            setToggling(null);
        }
    };

    if (loading) return (
        <>
        <CustomNavbar />
        <div className="text-center py-5"><Spinner animation="border" variant="light" /></div>
        </>
    );

    const licenseUI = license ? (LICENSE_STATUS_UI[license.status] || LICENSE_STATUS_UI.missing) : null;

    return (
        <>
        <CustomNavbar />
        <Container className="py-4" style={{ maxWidth: 960 }}>

            {/* ── Lizenz-Banner ── */}
            {!licenseLoading && license && (() => {
                const isError = license.enforced && ['missing', 'invalid'].includes(license.status);
                return (
                <div style={{
                    background: licenseUI.bg || 'rgba(255,255,255,0.04)',
                    border: `1px solid ${licenseUI.color}44`,
                    borderRadius: '1rem',
                    padding: '1rem 1.25rem',
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap',
                }}>
                    <FaShieldAlt style={{ color: licenseUI.color, fontSize: '1.5rem', flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
                            Lizenz-Status:
                            <span style={{ color: licenseUI.color, marginLeft: 8 }}>
                                {licenseUI.icon} {licenseUI.label}
                            </span>
                            {license.customer && (
                                <span style={{ color: '#aaa', fontWeight: 400, marginLeft: 12, fontSize: '0.85rem' }}>
                                    — {license.customer}
                                </span>
                            )}
                        </div>
                        {license.expDate && (
                            <div style={{ color: '#aaa', fontSize: '0.82rem', marginTop: 2 }}>
                                {license.status === 'expired'
                                    ? `Abgelaufen am ${license.expDate}`
                                    : `Gültig bis ${license.expDate}${license.daysLeft != null ? ` (noch ${license.daysLeft} Tage)` : ''}`
                                }
                            </div>
                        )}
                        {isError && (
                            <div style={{ color: '#fca5a5', fontSize: '0.83rem', marginTop: 6, fontWeight: 500 }}>
                                <FaExclamationTriangle style={{ marginRight: 5 }} />
                                {license.message}
                                <br />
                                <span style={{ fontWeight: 400, color: '#aaa' }}>
                                    Alle API-Anfragen werden mit HTTP 402 blockiert. Bitte einen gültigen{' '}
                                    <code style={{ color: '#fca5a5' }}>LICENSE_KEY</code> in die <code style={{ color: '#fca5a5' }}>.env</code> eintragen und den Server neu starten.
                                </span>
                            </div>
                        )}
                        {!isError && license.status === 'missing' && (
                            <div style={{ color: '#aaa', fontSize: '0.82rem', marginTop: 2 }}>
                                Kein Lizenzschlüssel konfiguriert — läuft im Demo-Modus ohne Einschränkungen.
                            </div>
                        )}
                        {license.message && !['missing'].includes(license.status) && !isError && (
                            <div style={{ color: '#aaa', fontSize: '0.82rem', marginTop: 2 }}>{license.message}</div>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {(license.licensedModules || []).map(lm => (
                            <span key={lm.featureKey} style={{
                                background: 'rgba(255,255,255,0.08)',
                                color: '#ccc',
                                borderRadius: '0.4rem',
                                padding: '0.2rem 0.6rem',
                                fontSize: '0.75rem',
                            }}>
                                {MODULE_BY_KEY()[lm.moduleName]?.label || lm.moduleName}
                            </span>
                        ))}
                    </div>
                </div>
                );
            })()}

            <div className="mb-4">
                <h2 className="text-white mb-1">Feature-Verwaltung</h2>
                <p className="text-muted mb-0">
                    <FaInfoCircle className="me-1" />
                    Änderungen gelten für neue Seitenaufrufe. Eingeloggte Nutzer müssen die Seite neu laden.
                </p>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row className="g-4">
                {MODULES().map(mod => {
                    const entry    = entries[mod.key];
                    const isActive = entry?.value === '1';
                    const isLoading = toggling === mod.key;
                    const licensed  = isLicensed(mod);
                    const Icon      = mod.icon;

                    // Abhängigkeiten prüfen
                    const missingDeps = (mod.requires || []).filter(dep => entries[dep]?.value !== '1');
                    const dependentsActive = (MODULE_DEPENDENTS[mod.key] || []).filter(dep => entries[dep]?.value === '1');

                    return (
                        <Col key={mod.key} xs={12} md={6}>
                            <div style={{
                                background: isActive && licensed ? mod.gradient : 'rgba(255,255,255,0.02)',
                                border: `1px solid ${isActive && licensed ? mod.color + '40' : 'rgba(255,255,255,0.08)'}`,
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                transition: 'all 0.25s',
                                opacity: (licensed || !['valid','expiring','expired'].includes(license?.status)) ? 1 : 0.6,
                                position: 'relative',
                            }}>
                                {/* Lock overlay for unlicensed */}
                                {!licensed && ['valid','expiring','expired'].includes(license?.status) && (
                                    <div style={{
                                        position: 'absolute', top: 12, right: 12,
                                    }}>
                                        <span style={{
                                            background: 'rgba(248,113,113,0.15)',
                                            border: '1px solid rgba(248,113,113,0.3)',
                                            color: '#f87171',
                                            borderRadius: '0.4rem',
                                            padding: '0.2rem 0.6rem',
                                            fontSize: '0.72rem',
                                            fontWeight: 600,
                                        }}>
                                            <FaLock style={{ marginRight: 4, fontSize: '0.65rem' }} />
                                            Nicht lizenziert
                                        </span>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{
                                            width: 40, height: 40,
                                            borderRadius: '0.625rem',
                                            background: isActive ? mod.color + '25' : 'rgba(255,255,255,0.05)',
                                            border: `1px solid ${isActive ? mod.color + '50' : 'transparent'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Icon style={{ color: isActive ? mod.color : '#666', fontSize: '1.1rem' }} />
                                        </div>
                                        <div>
                                            <div className="text-white fw-semibold" style={{ lineHeight: 1.2, paddingRight: licensed ? 0 : 100 }}>
                                                {mod.label}
                                            </div>
                                            <Badge style={{
                                                background: isActive ? mod.color + '30' : 'rgba(255,255,255,0.08)',
                                                color: isActive ? mod.color : '#888',
                                                fontSize: '0.7rem', fontWeight: 500,
                                                border: `1px solid ${isActive ? mod.color + '40' : 'transparent'}`,
                                            }}>
                                                {isActive ? <><FaCheck className="me-1" />Aktiv</> : <><FaBan className="me-1" />Deaktiviert</>}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Toggle */}
                                    <button
                                        onClick={() => toggle(mod.key)}
                                        disabled={isLoading || !entry || (!licensed && ['valid','expiring','expired'].includes(license?.status))}
                                        title={!licensed ? 'Modul nicht lizenziert' : undefined}
                                        style={{
                                            width: 52, height: 28,
                                            borderRadius: 14,
                                            border: 'none',
                                            background: isActive ? mod.color : 'rgba(255,255,255,0.12)',
                                            cursor: (isLoading || !licensed) ? 'not-allowed' : 'pointer',
                                            position: 'relative',
                                            transition: 'background 0.2s',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isLoading ? (
                                            <Spinner animation="border" size="sm" style={{ color: '#fff', width: 14, height: 14, borderWidth: 2 }} />
                                        ) : (
                                            <div style={{
                                                width: 20, height: 20, borderRadius: '50%', background: '#fff',
                                                position: 'absolute', top: 4, left: isActive ? 28 : 4,
                                                transition: 'left 0.2s',
                                            }} />
                                        )}
                                    </button>
                                </div>

                                {/* Dependency badges */}
                                {(mod.requires?.length > 0 || dependentsActive.length > 0) && (
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                        {mod.requires?.map(dep => (
                                            <span key={dep} style={{
                                                background: missingDeps.includes(dep)
                                                    ? 'rgba(248,113,113,0.12)' : 'rgba(255,255,255,0.06)',
                                                color: missingDeps.includes(dep) ? '#f87171' : '#888',
                                                border: `1px solid ${missingDeps.includes(dep) ? 'rgba(248,113,113,0.3)' : 'rgba(255,255,255,0.1)'}`,
                                                borderRadius: '0.35rem', fontSize: '0.72rem',
                                                padding: '0.15rem 0.5rem',
                                            }}>
                                                <FaLink style={{ marginRight: 3, fontSize: '0.6rem' }} />
                                                Benötigt: {MODULE_BY_KEY()[dep]?.label || dep}
                                            </span>
                                        ))}
                                        {dependentsActive.map(dep => (
                                            <span key={dep} style={{
                                                background: 'rgba(250,204,21,0.08)',
                                                color: '#facc15', opacity: 0.8,
                                                border: '1px solid rgba(250,204,21,0.15)',
                                                borderRadius: '0.35rem', fontSize: '0.72rem',
                                                padding: '0.15rem 0.5rem',
                                            }}>
                                                <FaExclamationTriangle style={{ marginRight: 3, fontSize: '0.6rem' }} />
                                                Genutzt von: {MODULE_BY_KEY()[dep]?.label || dep}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Feature list */}
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {mod.features.map(f => (
                                        <li key={f.label} style={{
                                            padding: '0.35rem 0',
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                            display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                                        }}>
                                            <span style={{
                                                width: 6, height: 6, borderRadius: '50%',
                                                background: isActive ? mod.color : '#444',
                                                marginTop: 7, flexShrink: 0, transition: 'background 0.2s',
                                            }} />
                                            <div>
                                                <span style={{ color: isActive ? '#e0e0e0' : '#666', fontSize: '0.88rem', fontWeight: 500 }}>
                                                    {f.label}
                                                </span>
                                                <span style={{ color: '#555', fontSize: '0.78rem', marginLeft: 6 }}>
                                                    {f.desc}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {/* ── Notsperre ─────────────────────────────────────────────── */}
            <div style={{
                marginTop: '2.5rem',
                background: 'rgba(248,113,113,0.05)',
                border: '1px solid rgba(248,113,113,0.28)',
                borderRadius: '1rem',
                padding: '1.25rem 1.4rem',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                    <FaLock style={{ color: '#f87171' }} />
                    <h5 style={{ margin: 0, color: '#f2f3f5', fontSize: '1.02rem', fontWeight: 620 }}>
                        Zugang sperren
                    </h5>
                    {systemLocked && (
                        <span style={{
                            background: 'rgba(248,113,113,0.2)', color: '#fca5a5',
                            borderRadius: '0.4rem', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700,
                        }}>AKTIV</span>
                    )}
                </div>

                <p style={{ color: '#a2a8ae', fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '0.9rem' }}>
                    Sperrt die gesamte App — <b>einschließlich der Anmeldung</b>. {cfg.memberTermPlural} sehen
                    nur noch einen Hinweis. Gedacht für den Fall, dass der Betrieb eingestellt
                    werden soll, etwa nach Ablauf der Lizenz. Diese Funktion ist
                    Administratoren vorbehalten; {cfg.adminRoleName} kann sie nicht bedienen.
                </p>

                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '0.8rem', color: '#8b9096' }}>
                        Hinweistext für {cfg.memberTermPlural} (optional)
                    </Form.Label>
                    <Form.Control
                        size="sm"
                        value={lockMessage}
                        onChange={e => setLockMessage(e.target.value)}
                        placeholder="z. B. Der Zugang ist bis auf Weiteres gesperrt. Bitte wende dich an den Vorstand."
                        style={{ background: '#1a1d21', borderColor: 'rgba(255,255,255,0.12)', color: '#eceef0' }}
                    />
                </Form.Group>

                <Button
                    variant={systemLocked ? 'outline-success' : 'danger'}
                    size="sm"
                    disabled={lockBusy}
                    onClick={() => toggleSystemLock(!systemLocked)}
                >
                    {lockBusy
                        ? <Spinner size="sm" animation="border" />
                        : systemLocked ? <><FaUnlock className="me-1" />Sperre aufheben</> : <><FaLock className="me-1" />Zugang jetzt sperren</>}
                </Button>
            </div>
        </Container>
        </>
    );
}
